import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  User,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../auth/AuthContext';
import { BrandMark } from '../brand/BrandWordmark';
import { ADMIN_NAV_ITEMS, matchAdminNavPath } from './adminNav';

const COLLAPSE_KEY = 'becoder_admin_sidebar_collapsed';

export const AdminSidebar: React.FC = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const navBody = (compact: boolean) => (
    <>
      <div
        className={`px-2.5 pb-3 mb-2 border-b border-line dark:border-line-dark ${
          compact ? 'text-center' : ''
        }`}
      >
        {!compact ? (
          <Link
            to="/admin"
            className="flex items-center gap-2 min-w-0 hover:opacity-90 transition-opacity"
            aria-label="BeCoder"
          >
            <BrandMark className="h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-brand-500">
                BeCoder
              </p>
              <h2 className="text-[13px] font-semibold text-ink dark:text-paper mt-0.5 leading-tight truncate">
                {t('cc.controlCenter')}
              </h2>
            </div>
          </Link>
        ) : (
          <Link to="/admin" className="inline-flex justify-center" aria-label="BeCoder" title="BeCoder">
            <BrandMark className="h-5 w-5" />
          </Link>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto" aria-label={t('cc.controlCenter')}>
        <ul className="space-y-0.5">
          {ADMIN_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={Boolean(item.end)}
                  title={compact ? t(item.labelKey) : undefined}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => {
                    const active =
                      isActive || matchAdminNavPath(location.pathname, item.path, item.end);
                    return `group relative flex items-center gap-2 rounded-md text-[12px] font-medium transition-colors duration-150 ${
                      compact ? 'justify-center px-2 py-2' : 'px-2.5 py-1.5'
                    } ${
                      active
                        ? 'bg-brand-500/12 text-brand-700 dark:text-brand-300'
                        : 'text-ink/75 dark:text-paper/70 hover:bg-soft dark:hover:bg-ink-soft/60 hover:text-ink dark:hover:text-paper'
                    }`;
                  }}
                >
                  {({ isActive }) => {
                    const active =
                      isActive || matchAdminNavPath(location.pathname, item.path, item.end);
                    return (
                      <>
                        {active && !compact && (
                          <span
                            className="absolute start-0 top-1 bottom-1 w-0.5 rounded-full bg-brand-500"
                            aria-hidden
                          />
                        )}
                        <Icon
                          className="w-3.5 h-3.5 shrink-0 text-brand-500"
                          strokeWidth={active ? 2.25 : 1.75}
                        />
                        {!compact && <span className="truncate">{t(item.labelKey)}</span>}
                      </>
                    );
                  }}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div
        className={`mt-auto pt-3 border-t border-line dark:border-line-dark space-y-2 ${
          compact ? 'px-1' : 'px-1.5'
        }`}
      >
        {!compact && user && (
          <div className="px-1.5 py-1">
            <p className="text-[11px] font-semibold text-ink dark:text-paper truncate" dir="ltr">
              {user.email}
            </p>
            <p className="text-[9px] uppercase tracking-wide text-brand-600 dark:text-brand-400 mt-0.5">
              {t('cc.rolePlatformAdmin')}
            </p>
          </div>
        )}
        <div className={`flex ${compact ? 'flex-col items-center' : 'items-center'} gap-1`}>
          <Link
            to="/admin/account"
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium text-fog hover:text-ink dark:hover:text-paper hover:bg-soft dark:hover:bg-ink-soft/50"
            title={t('account.title')}
            onClick={() => setMobileOpen(false)}
          >
            <User className="w-3.5 h-3.5" />
            {!compact && t('account.title')}
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium text-fog hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10"
            title={t('portal.signOut')}
          >
            <LogOut className="w-3.5 h-3.5" />
            {!compact && t('portal.signOut')}
          </button>
        </div>
        <button
          type="button"
          className="hidden md:inline-flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[10px] font-semibold text-fog hover:bg-soft dark:hover:bg-ink-soft/50"
          onClick={() => setCollapsed((v) => !v)}
          aria-pressed={collapsed}
          aria-label={collapsed ? t('cc.expandSidebar') : t('cc.collapseSidebar')}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-3.5 h-3.5" />
          ) : (
            <PanelLeftClose className="w-3.5 h-3.5" />
          )}
          {!collapsed && t('cc.collapseSidebar')}
        </button>
      </div>
    </>
  );

  return (
    <>
      <div className="md:hidden px-3 pt-3">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-line dark:border-line-dark bg-white dark:bg-ink-elevated text-xs font-semibold"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-4 h-4 text-brand-500" /> : <Menu className="w-4 h-4 text-brand-500" />}
          {t('cc.controlCenter')}
        </button>
        {mobileOpen && (
          <aside className="mt-2 p-3 rounded-lg bg-white dark:bg-ink-elevated border border-line dark:border-line-dark shadow-[var(--bc-shadow)] flex flex-col max-h-[70vh]">
            {navBody(false)}
          </aside>
        )}
      </div>

      <aside
        className={`bc-cc-sidebar hidden md:flex flex-col shrink-0 p-3 min-h-[calc(100vh-3rem)] ${
          collapsed ? 'w-[3.75rem]' : 'w-52'
        }`}
      >
        {navBody(collapsed)}
      </aside>
    </>
  );
};
